import { Component, effect, inject, untracked } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';

import { ComponentAccess } from '../../../../common/decorators/component-access.decorator';
import { ProFeature } from '../../../../common/enums/pro-feature-message.enum';
import { AuthenticationService } from '../../../../common/services/authentication/authentication.service';
import { ProGateService } from '../../../core/payment/services/pro-gate.service';
import { SubscriptionService } from '../../../core/payment/services/subscription.service';
import { OnboardingService, TourId } from './onboarding.service';
import { WelcomeModalComponent, WelcomeResult } from './welcome-modal.component';

/**
 * Always-mounted, headless host (one instance, in AppComponent) that presents
 * the first-run welcome carousel — on first launch, and again whenever the menu
 * asks for a replay. Same split as `PaywallHostComponent`: a root service
 * (`OnboardingService`, which cannot inject `ModalController` here) holds the
 * state, and this component owns the modal.
 *
 * AppComponent only mounts it once the splash has finished and the cookie
 * prompt is settled, so the carousel can never cover a blocking prompt.
 */
@Component({
  selector: 'app-onboarding-host',
  standalone: true,
  template: '',
})
@ComponentAccess('OnboardingHostComponent')
export class OnboardingHostComponent {
  private readonly modalCtrl = inject(ModalController);
  private readonly onboarding = inject(OnboardingService);
  private readonly auth = inject(AuthenticationService);
  private readonly subscription = inject(SubscriptionService);
  private readonly proGate = inject(ProGateService);

  private presenting = false;

  constructor() {
    // First launch: mounting IS the trigger (AppComponent gates that).
    if (!this.onboarding.hasSeen(TourId.Welcome)) {
      void this.present();
    }

    // Menu → "Getting started". Skips the initial 0 so this doesn't double-fire
    // with the first-launch call above.
    effect(() => {
      const replays = this.onboarding.welcomeReplays();
      if (replays > 0) untracked(() => void this.present());
    });
  }

  private async present(): Promise<void> {
    if (this.presenting) return; // never stack carousels
    this.presenting = true;
    this.onboarding.welcomeOpen.set(true);
    try {
      const modal = await this.modalCtrl.create({
        component: WelcomeModalComponent,
        cssClass: 'center-popup-modal',
        // First-run explainer: a stray backdrop tap or a hardware back press
        // shouldn't silently burn the one time we get to explain the app.
        backdropDismiss: false,
      });
      await modal.present();
      const { data } = await modal.onDidDismiss<WelcomeResult>();

      // Marked on the way out, not on open: a user who never reaches the end
      // still shouldn't be asked twice, and a menu replay re-marking is a no-op.
      this.onboarding.markSeen(TourId.Welcome);
      if (data === 'tour') this.onboarding.requestTour(TourId.Shell);
      else this.maybeOfferWelcomePaywall();
    } finally {
      this.presenting = false;
      this.onboarding.welcomeOpen.set(false);
    }
  }

  /**
   * One-time post-onboarding Pro offer (ADR-0129). Shown once per account, to a
   * signed-in non-Pro user who finished the welcome carousel WITHOUT starting the
   * shell tour — so it never overlays the tour spotlight; tour-takers meet Pro at
   * a natural gate instead. Skippable (the paywall's own dismiss). The paywall
   * leads with the annual plan + free-trial CTA.
   */
  private maybeOfferWelcomePaywall(): void {
    const userId = this.auth.getUserId();
    if (!userId || this.subscription.isPro) return;
    if (this.onboarding.hasOfferedWelcomePaywall(userId)) return;
    // Purchasing switched off for this channel (ADR-0173): skip WITHOUT marking it
    // offered. Marking it would burn this user's one-and-only welcome offer on a
    // paywall that cannot open, and they would never see it again once the channel
    // comes back on.
    if (!this.proGate.purchasingEnabled()) return;
    this.onboarding.markWelcomePaywallOffered(userId);
    this.proGate.open(ProFeature.WelcomeOffer);
  }
}
